/**
 * Dependency-free validation for the three auth forms.
 *
 * Why this is not Zod: Login, ForgotPassword and ResetPassword are the only
 * eagerly-imported modules in the app that pulled `zod`, and every other
 * consumer (the dialogs, CompanyAdmin) is behind a lazy route. Zod v4 does not
 * tree-shake — its classic `z.*` API hangs off one deeply interconnected
 * namespace — so those three imports dragged the entire 258 kB (58 kB gzip)
 * library onto the /login critical path to check "is this an email" and "is
 * this password 6 characters".
 *
 * Removing them lets the zod chunk load lazily, with the dialogs that actually
 * need schema composition. Nothing else changes: the form types in those pages
 * were always hand-declared rather than `z.infer`, and the messages and
 * evaluation order below reproduce the schemas they replace exactly.
 */

import type { FieldValues, Resolver, ResolverResult } from "react-hook-form";

/** Zod v4's own "practical email validation" pattern, copied verbatim from
 *  zod/v4/core/regexes.js so acceptance is byte-for-byte what it was before.
 *  Do not "improve" this — the point is behavioural parity, and the server
 *  validates independently anyway. */
export const EMAIL_REGEX =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-\.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;

export function isEmail(value: string): boolean {
  return EMAIL_REGEX.test(value);
}

/** A failed check: which field, and what to say about it. */
export type Violation<T> = [field: keyof T & string, message: string];

/** Returns a Violation when the value is invalid, or null when it passes. */
export type Rule<T> = (values: T) => Violation<T> | null;

/**
 * Build a react-hook-form `resolver` from a list of rules.
 *
 * Matches what zodResolver did on these forms: rules run in order, the FIRST
 * failure for a given field is the one reported (react-hook-form's default
 * criteriaMode is "firstError"), and `values` comes back empty when anything
 * failed so RHF treats the submission as invalid.
 *
 * Typed as RHF's own `Resolver<T>` rather than a hand-rolled shape — its
 * ResolverResult is a discriminated union of success and failure, and matching
 * it exactly is what keeps `handleSubmit` correctly typed at the call sites.
 */
export function createResolver<T extends FieldValues>(rules: Rule<T>[]): Resolver<T> {
  return async values => {
    const errors: Record<string, { type: string; message: string }> = {};

    for (const rule of rules) {
      const violation = rule(values);
      if (!violation) continue;
      const [field, message] = violation;
      if (!errors[field]) errors[field] = { type: "validation", message };
    }

    return Object.keys(errors).length > 0
      ? { values: {}, errors: errors as ResolverResult<T>["errors"] }
      : { values, errors: {} };
  };
}
